import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as ApmService from "../../services/Apm";

const initialState = {
  // Active session being monitored
  activeSession: null,
  // Live snapshot data (polled during active session)
  liveSnapshot: null,
  // Completed session details + data points
  sessionDetail: null,
  // List of sessions (history)
  sessions: [],
  sessionsTotal: 0,
  // Loading states
  startLoading: false,
  stopLoading: false,
  snapshotLoading: false,
  sessionLoading: false,
  listLoading: false,
  deleteLoading: false,
  // Errors
  error: null,
};

export const startApmSession = createAsyncThunk(
  "apm/startSession",
  async ({ targetType, targetId, name, config }, { rejectWithValue }) => {
    try {
      const response = await ApmService.startApmSession(
        targetType,
        targetId,
        name,
        config,
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to start APM session",
      );
    }
  },
);

export const stopApmSession = createAsyncThunk(
  "apm/stopSession",
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await ApmService.stopApmSession(sessionId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to stop APM session",
      );
    }
  },
);

export const fetchApmSnapshot = createAsyncThunk(
  "apm/fetchSnapshot",
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await ApmService.getApmSnapshot(sessionId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.message ||
          "Failed to get snapshot",
      );
    }
  },
);

export const fetchApmSession = createAsyncThunk(
  "apm/fetchSession",
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await ApmService.getApmSession(sessionId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || error.message || "Failed to get session",
      );
    }
  },
);

export const fetchApmSessions = createAsyncThunk(
  "apm/fetchSessions",
  async (params, { rejectWithValue }) => {
    try {
      const response = await ApmService.listApmSessions(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.message ||
          "Failed to list sessions",
      );
    }
  },
);

export const deleteApmSession = createAsyncThunk(
  "apm/deleteSession",
  async (sessionId, { rejectWithValue }) => {
    try {
      await ApmService.deleteApmSession(sessionId);
      return sessionId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.message ||
          "Failed to delete session",
      );
    }
  },
);

const apmSlice = createSlice({
  name: "apm",
  initialState,
  reducers: {
    clearApmError: (state) => {
      state.error = null;
    },
    clearActiveSession: (state) => {
      state.activeSession = null;
      state.liveSnapshot = null;
    },
    clearSessionDetail: (state) => {
      state.sessionDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Start session
      .addCase(startApmSession.pending, (state) => {
        state.startLoading = true;
        state.error = null;
      })
      .addCase(startApmSession.fulfilled, (state, action) => {
        state.startLoading = false;
        state.activeSession = action.payload.session;
      })
      .addCase(startApmSession.rejected, (state, action) => {
        state.startLoading = false;
        state.error = action.payload;
      })
      // Stop session
      .addCase(stopApmSession.pending, (state) => {
        state.stopLoading = true;
        state.error = null;
      })
      .addCase(stopApmSession.fulfilled, (state, action) => {
        state.stopLoading = false;
        state.activeSession = null;
        state.liveSnapshot = null;
        state.sessionDetail = action.payload.session;
      })
      .addCase(stopApmSession.rejected, (state, action) => {
        state.stopLoading = false;
        state.error = action.payload;
      })
      // Fetch snapshot
      .addCase(fetchApmSnapshot.pending, (state) => {
        state.snapshotLoading = true;
      })
      .addCase(fetchApmSnapshot.fulfilled, (state, action) => {
        state.snapshotLoading = false;
        state.liveSnapshot = action.payload;
      })
      .addCase(fetchApmSnapshot.rejected, (state, action) => {
        state.snapshotLoading = false;
        state.error = action.payload;
      })
      // Fetch session detail
      .addCase(fetchApmSession.pending, (state) => {
        state.sessionLoading = true;
        state.error = null;
      })
      .addCase(fetchApmSession.fulfilled, (state, action) => {
        state.sessionLoading = false;
        state.sessionDetail = action.payload;
        // If session is active, set it as activeSession too
        if (action.payload.session?.status === "ACTIVE") {
          state.activeSession = action.payload.session;
        }
      })
      .addCase(fetchApmSession.rejected, (state, action) => {
        state.sessionLoading = false;
        state.error = action.payload;
      })
      // List sessions
      .addCase(fetchApmSessions.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(fetchApmSessions.fulfilled, (state, action) => {
        state.listLoading = false;
        state.sessions = action.payload.sessions;
        state.sessionsTotal = action.payload.total;
      })
      .addCase(fetchApmSessions.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload;
      })
      // Delete session
      .addCase(deleteApmSession.pending, (state) => {
        state.deleteLoading = true;
      })
      .addCase(deleteApmSession.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.sessions = state.sessions.filter((s) => s.id !== action.payload);
        state.sessionsTotal = Math.max(0, state.sessionsTotal - 1);
      })
      .addCase(deleteApmSession.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearApmError, clearActiveSession, clearSessionDetail } =
  apmSlice.actions;

export default apmSlice.reducer;
